// src/AppRouter.jsx
import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Typography,Container, Grid, Card, CardActionArea, CardContent, Chip } from '@mui/material';

// Hooks e serviços
import useAuth from './hooks/useAuth';
import api from './services/api';
import { useRole } from './contexts/RoleContext';

// Páginas públicas básicas
import HomePage from './pages/HomePage';
import LoginPage from './pages/Auth/LoginPage';
import RegisterPage from './pages/Auth/RegisterPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage';
import VerifyEmailPage from './pages/Auth/VerifyEmailPage';
import ResendVerificationPage from './pages/Auth/ResendVerificationPage';
import ServicosPage from './pages/Service/ServicosPage';
import FAQPage from './pages/Legal/FAQPage';
import PrivacidadePage from './pages/Legal/PrivacidadePage';
import TermosDeUsoPage from './pages/Legal/TermosDeUsoPage';
import CentroAjudaPage from './pages/Support/CentroAjudaPage';
import BlogPage from './pages/Blog/BlogPage';
import ParceirosPage from './pages/About/ParceirosPage';
import QuemSomosPage from './pages/About/QuemSomosPage';
import NossaEquipePage from './pages/About/NossaEquipePage';
import CarreirasPage from './pages/About/CarreirasPage';
import SuportePage from './pages/Support/SuportePage';
import LeadCapturePage from './pages/Leads/LeadCapturePage';
import RecomendacoesPage from './pages/RecomendacoesPage';
import VisualizarInstituicaoPage from './pages/Instituicoes/VisualizarInstituicaoPage';

// Dashboards centrais
import AdminDashboardPage from './pages/Dashboard/AdminDashboardPage';
import InstituicaoDashboardPage from './pages/Dashboard/InstituicaoDashboardPage';
import EstudanteDashboardPage from './pages/Dashboard/EstudanteDashboardPage';
import CientificoDashboardPage from './pages/Dashboard/CientificoDashboardPage';
import SupervisorDashboardPage from './pages/Dashboard/SupervisorDashboardPage';
import PresidenteDashboardPage from './pages/Dashboard/PresidenteDashboardPage';
import ArguenteDashboardPage from './pages/Dashboard/ArguenteDashboardPage';
import ChefeDepartamentoDashboardPage from './pages/Dashboard/ChefeDepartamentoDashboardPage';
import RevisorDashboardPage from './pages/Dashboard/RevisorDashboardPage';

// 📚 CRECHE
import DashboardCrechePage from './pages/creche/DashboardCrechePage';
import NovaMatriculaCrechePage from './pages/creche/NovaMatriculaCrechePage';
import ListaMatriculasCrechePage from './pages/creche/ListaMatriculasCrechePage';
import CheckinsPage from './pages/creche/CheckinsPage';
import PresencasPage from './pages/creche/PresencasPage';
import ResponsaveisPage from './pages/creche/ResponsaveisPage';
import SaudePage from './pages/creche/SaudePage';
import RelatoriosCrechePage from './pages/creche/RelatoriosCrechePage';
import ResponsavelDashboardPage from './pages/creche/ResponsavelDashboardPage';
import PreInscricaoPublicPage from './pages/creche/PreInscricaoPublicPage';
import PreInscricoesAdminPage from './pages/creche/PreInscricoesAdminPage';
import CrecheHomePage from './pages/creche/CrecheHomePage';





// Páginas de perfil/instituições
import CompletarPerfilPage from './pages/CompletarPerfilPage';
import GerenciarPerfisPage from './pages/Instituicoes/GerenciarPerfisPage';
import ListaInstituicoesPage from './pages/Instituicoes/ListaInstituicoesPage';
import DetalhesInstituicaoPage from './pages/Instituicoes/DetalhesInstituicaoPage';
import CriarEditarInstituicaoPage from './pages/Instituicoes/CriarEditarInstituicaoPage';
import GerenciarSlidersPage from './pages/Instituicoes/GerenciarSlidersPage';
import GerenciarDestaquesPage from './pages/Instituicoes/GerenciarDestaquesPage';
import PostsPage from './pages/Instituicoes/PostsPage';
import PostDetalhePage from './pages/Instituicoes/PostDetalhePage';
import DefinirMensagemConfirmacao from './pages/Instituicoes/DefinirMensagemConfirmacao';

// Convites & vínculos
import GeradorCodigosConvite from './pages/Convites/GeradorCodigosConvite';
import EstudantesVinculadosPage from './pages/Instituicoes/EstudantesVinculadosPage';
import VinculosInstitucionaisPage from './pages/Estudantes/VinculosInstitucionaisPage';

// Pré-inscrições e estudantes
import ListaPreInscricoesPorClasseCursoPage from './pages/PreInscricoes/ListaPreInscricoesPorClasseCursoPage';
import ListaPreInscricoesPage from './pages/PreInscricoes/ListaPreInscricoesPage';
import DetalhesPreInscricaoPage from './pages/PreInscricoes/DetalhesPreInscricaoPage';
import CriarPreInscricaoPage from './pages/PreInscricoes/CriarPreInscricaoPage';
import ComprovativoPreInscricaoPage from './pages/PreInscricoes/ComprovativoPreInscricaoPage';
import ListaEstudantesPage from './pages/Estudantes/ListaEstudantesPage';
import DetalhesEstudantePage from './pages/Estudantes/DetalhesEstudantePage';
import CriarEditarEstudantePage from './pages/Estudantes/CriarEditarEstudantePage';

// Campanhas
import ListaCampanhasPage from './pages/Campanhas/ListaCampanhasPage';
import DetalhesCampanhaPage from './pages/Campanhas/DetalhesCampanhaPage';
import CriarEditarCampanhaPage from './pages/Campanhas/CriarEditarCampanhaPage';

// Cursos / Períodos / Classes / Disciplinas
import ListaCursosPage from './pages/Cursos/ListaCursosPage';
import DetalhesCursoPage from './pages/Cursos/DetalhesCursoPage';
import CriarEditarCursoPage from './pages/Cursos/CriarEditarCursoPage';
import ListaPeriodosPage from './pages/Periodos/ListaPeriodosPage';
import DetalhesPeriodoPage from './pages/Periodos/DetalhesPeriodoPage';
import CriarEditarPeriodoPage from './pages/Periodos/CriarEditarPeriodoPage';
import ListaClassesPage from './pages/Classes/ListaClassesPage';
import DetalhesClassePage from './pages/Classes/DetalhesClassePage';
import CriarEditarClassePage from './pages/Classes/CriarEditarClassePage';
import ListaDisciplinasPage from './pages/Disciplinas/ListaDisciplinasPage';
import DetalhesDisciplinaPage from './pages/Disciplinas/DetalhesDisciplinaPage';
import CriarEditarDisciplinaPage from './pages/Disciplinas/CriarEditarDisciplinaPage';

// Relatórios
import RelatorioInstituicaoPage from './pages/Relatorios/RelatorioInstituicaoPage';
import RelatorioGeralPage from './pages/Relatorios/RelatorioGeralPage';
import RelatorioFinanceiroPage from './pages/Relatorios/RelatorioFinanceiroPage';

// Planos de pagamento
import ListaPlanosPagamento from './pages/PlanosPagamento/ListaPlanosPagamento';
import CriarPlanoPagamento from './pages/PlanosPagamento/CriarPlanoPagamento';
import EditarPlanoPage from './pages/PlanosPagamento/EditarPlanoPage';
import DetalhesPlanoPage from './pages/PlanosPagamento/DetalhesPlanoPage';
import VincularEstudantePage from './pages/PlanosPagamento/VincularEstudantePage';
import SelecionarPlanoBasePage from './pages/PlanosPagamento/SelecionarPlanoBasePage';
import PlanosPagamentoEstudante from './pages/Estudantes/PlanosPagamentoEstudante';
import EstudantesPlanoLista from './components/PlanosPagamento/EstudantesPlanoLista';
import EstudantesPlanoDetalhes from './components/PlanosPagamento/EstudantesPlanoDetalhes';
import FaturaProforma from './components/Faturas/FaturaProforma';
import PagamentoPublico from './pages/PagamentoPublico';
import StudentFaturaRoute from './components/Routes/StudentFaturaRoute';
import PublicFaturaRoute from './routes/PublicFaturaRoute';

// Logs
import LogsPage from './pages/Admin/LogsPage';
import LogForensePage from './pages/Admin/LogForensePage';
import LogAcessoPage from './pages/Admin/LogAcessoPage';

// Billing / Módulos
import PainelComprovativos from './pages/Admin/PainelComprovativos';
import GestaoInstituicaoPage from './pages/Admin/GestaoInstituicaoPage';
import SolicitarModulo from './pages/Pagamentos/SolicitarModulo';

// TCC – propostas / projetos / monografias / defesas
import PropostaPage from './pages/Proposta/PropostaPage';
import SubmeterPropostaPage from './pages/Proposta/SubmeterPropostaPage';
import PropostaDetalhesPage from './pages/Proposta/PropostaDetalhesPage';
import ProjetoPage from './pages/Projeto/ProjetoPage';
import SubmeterProjetoPage from './pages/Projeto/SubmeterProjetoPage';
import ProjetoDetailsPage from './pages/Projeto/ProjetoDetailsPage';
import MonografiaPage from './pages/Monografia/MonografiaPage';
import SubmeterMonografiaPage from './pages/Monografia/SubmeterMonografiaPage';
import MonografiaDetails from './components/Monografia/MonografiaDetails';
import DefesaPage from './pages/Defesa/DefesaPage';
import AgendarDefesaPage from './pages/Defesa/AgendarDefesaPage';
import DefesaDetails from './components/Defesa/DefesaDetails';
import DashboardTCC from './pages/DashboardTCC';
import TCCPage from './pages/TCC/TCCPage';
import TCCInstituicaoOverviewPage from './pages/TCC/TCCInstituicaoOverviewPage';
import TCCHomePage from './pages/TCC/TCCHomePage';
import IntegridadePage from './pages/Integridade/IntegridadePage';

// Avaliação, presidente, arguente, supervisor, notificações
import AvaliacaoPage from './pages/Avaliacao/AvaliacaoPage';
import RegistrarAvaliacaoPage from './pages/Avaliacao/RegistrarAvaliacaoPage';
import AvaliacaoDetails from './components/Avaliacao/AvaliacaoDetails';
import PresidentePage from './pages/Presidente/PresidentePage';
import NomearPresidentePage from './pages/Presidente/NomearPresidentePage';
import PresidenteDetails from './components/Presidente/PresidenteDetails';
import ArguentePage from './pages/Arguente/ArguentePage';
import RegistrarArguentePage from './pages/Arguente/RegistrarArguentePage';
import ArguenteDetails from './components/Arguente/ArguenteDetails';
import SupervisorPage from './pages/Supervisor/SupervisorPage';
import RegistrarSupervisorPage from './pages/Supervisor/RegistrarSupervisorPage';
import NotificacaoPage from './pages/NotificacaoAcademica/NotificacaoPage';

// Repositório digital
import RepositorioPage from './pages/RepositorioPage';

// Unidades Orgânicas
import UnidadeOrganicaPage from './pages/UnidadeOrganica/UnidadeOrganicaPage';
import CriarUnidadeOrganicaPage from './pages/UnidadeOrganica/CriarUnidadeOrganicaPage';
import UnidadeOrganicaDetails from './components/UnidadeOrganica/UnidadeOrganicaDetails';

/* =================================================================== */
/*  Hook para verificação de vínculo institucional (frontend)          */
/* =================================================================== */
const useVinculoInstitucional = () => {
  const { user } = useAuth();
  const [cachedResults, setCachedResults] = useState({});

  const verificarVinculo = async (instituicaoId) => {
    if (!user || !user.id || user.tipo !== 'estudante') return true;

    const cacheKey = `inst-${instituicaoId}`;
    if (cachedResults[cacheKey] !== undefined) {
      return cachedResults[cacheKey];
    }

    try {
      let estudanteId = user.estudanteId;
      if (!estudanteId) {
        try {
          const { data } = await api.get(`/estudantes/usuario/${user.id}`);
          estudanteId = data.id;
          if (estudanteId) user.estudanteId = estudanteId;
        } catch (err) {
          console.error('Erro ao obter estudanteId:', err);
          setCachedResults(prev => ({ ...prev, [cacheKey]: false }));
          return false;
        }
      }

      if (!estudanteId) {
        console.error('Não foi possível determinar o ID do estudante');
        setCachedResults(prev => ({ ...prev, [cacheKey]: false }));
        return false;
      }

      const { data } = await api.get(
        `/vinculos/estudante/${estudanteId}/instituicao/${instituicaoId}`
      );

      setCachedResults(prev => ({ ...prev, [cacheKey]: data.hasAccess }));
      return data.hasAccess;
    } catch (err) {
      console.error('Erro ao verificar vínculo institucional:', err);
      setCachedResults(prev => ({ ...prev, [cacheKey]: false }));
      return false;
    }
  };

  return { verificarVinculo };
};

/* ======================================================================== */
/*  PrivateRoute – agora também aceita papéis do RoleContext                */
/* ======================================================================== */
/* ======================================================================== */
/*  PrivateRoute – agora respeita currentRole do RoleContext                */
/* ======================================================================== */
const PrivateRoute = ({
  children,
  allowedRoles,
  requiredPermissions = [],
  requiredModules = [],
  requiresVinculo = false,
}) => {
  const { user, checkModuleAccess } = useAuth();
  const {
    roles: ctxRoles = [],
    currentRole,
    loading: rolesLoading,
  } = useRole();

  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [hasModuleAccess, setHasModuleAccess] = useState(true);
  const [hasVinculoAccess, setHasVinculoAccess] = useState(true);

  useEffect(() => {
    const checkAccess = async () => {
      if (user && requiredModules.length > 0) {
        try {
          const results = await Promise.all(
            requiredModules.map((module) => checkModuleAccess(module))
          );
          setHasModuleAccess(results.some((ok) => ok));
        } catch (err) {
          console.error('Erro ao verificar módulos:', err);
          setHasModuleAccess(false);
        }
      }

      if (requiresVinculo && user?.tipo === 'estudante') {
        try {
          const { default: api } = await import('./services/api');
          const { data } = await api.get(`/vinculos/estudante/${user.estudanteId}/ativo`);
          setHasVinculoAccess(!!data && !data.mensagem);
        } catch {
          setHasVinculoAccess(false);
        }
      }

      setLoading(false);
    };

    checkAccess();
  }, [user, requiredModules, requiresVinculo, location, checkModuleAccess]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const isAdminOrAuditor = ['ADMIN_INSTITUICAO','AUDITOR_INTERNO'].includes(
    user.perfilAdmin
  );

  // 🔑 Ajuste crítico: respeitar o papel atual do RoleContext
  const effectiveRole =
    currentRole && currentRole !== 'auto' ? currentRole : user.tipo;

  const hasRole = allowedRoles
    ? (
        allowedRoles.includes(effectiveRole) || // 👈 aqui usamos o papel ativo
        allowedRoles.includes(user.perfilAdmin) ||
        (!rolesLoading && allowedRoles.some((r) => ctxRoles.includes(r)))
      )
    : true;

  const hasPermissions =
    isAdminOrAuditor ||
    requiredPermissions.length === 0 ||
    requiredPermissions.some((perm) =>
      user.perfilAtivo?.permissoes?.includes(perm)
    );

  if (!hasRole || !hasPermissions || !hasModuleAccess || !hasVinculoAccess) {
    console.log('🚫 PrivateRoute BLOQUEANDO acesso', {
      effectiveRole,
      userTipo: user.tipo,
      perfilAdmin: user.perfilAdmin,
      allowedRoles,
      ctxRoles,
      requiredPermissions,
      requiredModules,
      hasRole,
      hasPermissions,
      hasModuleAccess,
      hasVinculoAccess,
    });
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};
PrivateRoute.propTypes = {
  children: PropTypes.node.isRequired,
  allowedRoles: PropTypes.arrayOf(PropTypes.string),
  requiredPermissions: PropTypes.arrayOf(PropTypes.string),
  requiredModules: PropTypes.arrayOf(PropTypes.string),
  requiresVinculo: PropTypes.bool,
};

/* ======================================================================== */
/*  Painel unificado (auto) – seleção de papel                              */
/* ======================================================================== */
/* ======================================================================== */
/*  Painel unificado MELHORADO - seleção de papel                          */
/* ======================================================================== */
const labels = {
  admin: 'Administrador',
  instituicao: 'Instituição',
  estudante: 'Estudante',
  supervisor: 'Supervisor',
  arguente: 'Arguente',
  presidente: 'Presidente',
  chefe_departamento: 'Chefe de Departamento',
  revisor_projeto: 'Revisor de Projeto',
  DIRECAO_CIENTIFICA: 'Direção Científica',
  direcao_cientifica: 'Direção Científica',
};

const descriptions = {
  admin: 'Gestão completa do sistema e utilizadores',
  instituicao: 'Administração institucional e académica',
  estudante: 'Acesso aos seus trabalhos e submissões',
  supervisor: 'Orientação e acompanhamento de estudantes',
  arguente: 'Avaliação de trabalhos finais e defesas',
  presidente: 'Presidência de júris e comissões',
  chefe_departamento: 'Gestão departamental e aprovações',
  revisor_projeto: 'Revisão técnica de projetos académicos',
  DIRECAO_CIENTIFICA: 'Direção de atividades científicas',
  direcao_cientifica: 'Direção de atividades científicas',
};

const roleIcons = {
  admin: '👨‍💼',
  instituicao: '🏛️',
  estudante: '🎓',
  supervisor: '👩‍🏫',
  arguente: '📝',
  presidente: '⚖️',
  chefe_departamento: '🏢',
  revisor_projeto: '🔍',
  DIRECAO_CIENTIFICA: '🔬',
  direcao_cientifica: '🔬',
};

const roleColors = {
  admin: { primary: '#1976d2', secondary: '#e3f2fd' },
  instituicao: { primary: '#388e3c', secondary: '#e8f5e8' },
  estudante: { primary: '#f57c00', secondary: '#fff3e0' },
  supervisor: { primary: '#7b1fa2', secondary: '#f3e5f5' },
  arguente: { primary: '#d32f2f', secondary: '#ffebee' },
  presidente: { primary: '#303f9f', secondary: '#e8eaf6' },
  chefe_departamento: { primary: '#455a64', secondary: '#eceff1' },
  revisor_projeto: { primary: '#00796b', secondary: '#e0f2f1' },
  DIRECAO_CIENTIFICA: { primary: '#e64a19', secondary: '#fbe9e7' },
  direcao_cientifica: { primary: '#e64a19', secondary: '#fbe9e7' },
};

function RoleUnifiedLanding({ roles, onSelect }) {
  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 50%, #0277bd 100%)',
      py: 4
    }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ 
          textAlign: 'center', 
          mb: 5,
          color: 'white'
        }}>
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom
            sx={{ 
              fontWeight: 700,
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              mb: 2
            }}
          >
            Selecione o Seu Papel
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              opacity: 0.9,
              maxWidth: '600px',
              margin: '0 auto',
              fontWeight: 300
            }}
          >
            Tem acesso a múltiplos papéis. Escolha como pretende aceder ao sistema
          </Typography>
        </Box>

        {/* Role Cards */}
        {roles.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Chip 
              label="Não há papéis associados a este utilizador" 
              size="large"
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)', 
                color: 'white',
                fontSize: '1.1rem',
                py: 2
              }} 
            />
          </Box>
        ) : (
          <Grid container spacing={3} justifyContent="center">
            {roles.map((role) => {
              const colors = roleColors[role] || { primary: '#666', secondary: '#f5f5f5' };
              
              return (
                <Grid item key={role} xs={12} sm={6} md={4} lg={3}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: 3,
                      border: 'none',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                      '&:hover': {
                        transform: 'translateY(-8px) scale(1.02)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                        '& .role-icon': {
                          transform: 'scale(1.2) rotate(5deg)',
                        },
                        '& .role-overlay': {
                          opacity: 1,
                        }
                      }
                    }}
                    onClick={() => onSelect(role)}
                  >
                    {/* Background Overlay */}
                    <Box 
                      className="role-overlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: `linear-gradient(135deg, ${colors.primary}15, ${colors.primary}08)`,
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                      }}
                    />
                    
                    <CardContent sx={{ 
                      p: 3, 
                      textAlign: 'center',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      {/* Icon */}
                      <Box 
                        className="role-icon"
                        sx={{ 
                          fontSize: '3rem',
                          mb: 2,
                          transition: 'all 0.3s ease',
                          display: 'block'
                        }}
                      >
                        {roleIcons[role] || '👤'}
                      </Box>

                      {/* Title */}
                      <Typography 
                        variant="h6" 
                        component="h3"
                        gutterBottom
                        sx={{ 
                          fontWeight: 600,
                          color: colors.primary,
                          mb: 1
                        }}
                      >
                        {labels[role] || role}
                      </Typography>

                      {/* Description */}
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          lineHeight: 1.5,
                          minHeight: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {descriptions[role] || `Aceder como ${labels[role] || role}`}
                      </Typography>

                      {/* Action Indicator */}
                      <Box sx={{ 
                        mt: 2, 
                        pt: 2, 
                        borderTop: `1px solid ${colors.secondary}`
                      }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: colors.primary,
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}
                        >
                          Clique para aceder
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Footer Info */}
        <Box sx={{ 
          textAlign: 'center', 
          mt: 5, 
          color: 'rgba(255,255,255,0.8)' 
        }}>
          <Typography variant="body2">
            Pode alternar entre papéis a qualquer momento através do menu de perfil
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

RoleUnifiedLanding.propTypes = {
  roles: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelect: PropTypes.func.isRequired,
};
/* ======================================================================== */
/*  Seleção dinâmica do dashboard (usa RoleContext)                         */
/* ======================================================================== */
/* ======================================================================== */
/*  DashboardRouter - VERSÃO COMPLETA COM DEBUG                            */
/* ======================================================================== */
const DashboardRouter = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { roles = [], currentRole, loading, setRole } = useRole();
  const { pathname } = useLocation();

  // Papéis com rotas dedicadas
  const pathFor = (role) => {
    switch (role) {
      case 'responsavel':        return '/responsavel/creche'; 
      case 'presidente':         return '/dashboard/presidente';
      case 'supervisor':         return '/dashboard/supervisor';
      case 'arguente':           return '/dashboard/arguente';
      case 'chefe_departamento': return '/dashboard/chefe-departamento';
      case 'revisor_projeto':    return '/dashboard/revisor';
      case 'DIRECAO_CIENTIFICA':
      case 'direcao_cientifica': return '/dashboard/cientifico';
      default:                   return null; // admin, instituicao, estudante rendem direto
    }
  };

  console.log('🔄 DashboardRouter executando - currentRole:', currentRole, 'rolesLoading:', loading, 'roles:', roles);

  if (!user?.tipo) return <Navigate to="/login" />;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Carregando papéis...</Typography>
      </Box>
    );
  }

  const renderForRole = (role) => {
    if (!role) return <Navigate to="/" replace />;

    if (role === 'admin' || role === 'SUPER_ADMIN' || role === 'super_admin') return <AdminDashboardPage />;
    if (role === 'instituicao')  return <InstituicaoDashboardPage />;
    if (role === 'estudante')    return <EstudanteDashboardPage />;

    const target = pathFor(role);
    if (target && pathname !== target) {
      console.log('➡️ Navigate para', target, '(pathname atual:', pathname, ')');
      return <Navigate to={target} replace />;
    }

    // Já estamos na rota certa → deixa o filho renderizar
    return null;
  };

  // Só 1 papel → direto
  if (roles && roles.length === 1) {
    return renderForRole(roles[0]);
  }

  // Múltiplos papéis
  if (roles && roles.length > 1) {
    if (currentRole && currentRole !== 'auto') {
      return renderForRole(currentRole);
    }

    // Mostrar seletor
    return (
      <RoleUnifiedLanding
        roles={roles}
        onSelect={(r) => {
          console.log('🎯 Utilizador selecionou papel:', r);
          setRole(r);
          const target = pathFor(r);
          // Papéis derivados vão directo, base continuam em /dashboard
          navigate(target || '/dashboard', { replace: true });
        }}
      />
    );
  }

  // fallback
  return renderForRole(user.tipo);
};

/* ======================================================================== */
/*  Definição de rotas                                                      */
/* ======================================================================== */
const AppRouter = () => (
  <Routes>
    {/* PÚBLICAS */}
    <Route path="/" element={<HomePage />} />
    <Route path="/login" element={<LoginPage />} />
    <Route path="/creche" element={<CrecheHomePage />} />
    <Route path="/pre-inscricao" element={<PreInscricaoPublicPage />} />
    <Route path="/registro" element={<RegisterPage />} />
    <Route path="/unauthorized" element={<UnauthorizedPage />} />
    <Route path="/instituicoes/:id/visualizar" element={<VisualizarInstituicaoPage />} />
    <Route path="/leads" element={<LeadCapturePage />} />
    <Route path="/faturas/proforma/:prestacaoId" element={<PublicFaturaRoute><FaturaProforma /></PublicFaturaRoute>} />
    <Route path="/pagamento-publico/:prestacaoId" element={<PublicFaturaRoute><PagamentoPublico /></PublicFaturaRoute>} />
    <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
    <Route path="/reset-senha/:token" element={<ResetPasswordPage />} />
    <Route path="/verificar-email/:token" element={<VerifyEmailPage />} />
    <Route path="/reenviar-verificacao" element={<ResendVerificationPage />} />
    <Route path="/servicos" element={<ServicosPage />} />
    <Route path="/faq" element={<FAQPage />} />
    <Route path="/privacidade" element={<PrivacidadePage />} />
    <Route path="/termos-de-uso" element={<TermosDeUsoPage />} />
    <Route path="/centro-de-ajuda" element={<CentroAjudaPage />} />
    <Route path="/blog" element={<BlogPage />} />
    <Route path="/parceiros" element={<ParceirosPage />} />
    <Route path="/quem-somos" element={<QuemSomosPage />} />
    <Route path="/nossa-equipe" element={<NossaEquipePage />} />
    <Route path="/carreiras" element={<CarreirasPage />} />
    <Route path="/suporte" element={<SuportePage />} />

    {/* CONVITES & VÍNCULOS */}
    <Route
      path="/instituicoes/:instituicaoId/convites"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <GeradorCodigosConvite />
        </PrivateRoute>
      }
    />
    <Route
      path="/instituicoes/:instituicaoId/estudantes-vinculados"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <EstudantesVinculadosPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/meus-vinculos"
      element={
        <PrivateRoute allowedRoles={['estudante']}>
          <VinculosInstitucionaisPage />
        </PrivateRoute>
      }
    />

    {/* PERFIS & DASHBOARD ROOT */}
    <Route
      path="/instituicoes/:instituicaoId/perfis"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <GerenciarPerfisPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/dashboard"
      element={
        <PrivateRoute>
          <DashboardRouter />
        </PrivateRoute>
      }
    />

    {/* LOGS */}
    <Route
      path="/admin/logs"
      element={
        <PrivateRoute allowedRoles={['admin', 'SUPER_ADMIN']}>
          <LogsPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/admin/logs/forense"
      element={
        <PrivateRoute allowedRoles={['SUPER_ADMIN']}>
          <LogForensePage />
        </PrivateRoute>
      }
    />
    <Route
      path="/admin/logs/acesso"
      element={
        <PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN_INSTITUICAO']}>
          <LogAcessoPage />
        </PrivateRoute>
      }
    />

    {/* BILLING — Comprovativos (admin Zenkroll) */}
    <Route
      path="/admin/comprovativos"
      element={
        <PrivateRoute allowedRoles={['SUPER_ADMIN']}>
          <PainelComprovativos />
        </PrivateRoute>
      }
    />

    {/* BILLING — Gestão por instituição (admin Zenkroll) */}
    <Route
      path="/admin/instituicoes/:id"
      element={
        <PrivateRoute allowedRoles={['SUPER_ADMIN']}>
          <GestaoInstituicaoPage />
        </PrivateRoute>
      }
    />

    {/* BILLING — Solicitar módulo (admin da instituição) */}
    <Route
      path="/modulos/subscrever"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao', 'SUPER_ADMIN']}>
          <SolicitarModulo />
        </PrivateRoute>
      }
    />

    {/* STUDENT FATURA (protegida) */}
    <Route
      path="/faturas/proforma/:prestacaoId"
      element={
        <StudentFaturaRoute>
          <FaturaProforma />
        </StudentFaturaRoute>
      }
    />

    {/* =================================================================== */}
    {/*  INSTITUIÇÕES – rotas administrativas                               */}
    {/* =================================================================== */}
    <Route
      path="/instituicoes"
      element={
        <PrivateRoute allowedRoles={['admin']}>
          <ListaInstituicoesPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/instituicoes/criar"
      element={
        <PrivateRoute allowedRoles={['admin']}>
          <CriarEditarInstituicaoPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/instituicoes/editar/:id"
      element={
        <PrivateRoute allowedRoles={['admin']}>
          <CriarEditarInstituicaoPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/instituicoes/:id"
      element={
        <PrivateRoute allowedRoles={['admin', 'SUPER_ADMIN', 'instituicao']}>
          <DetalhesInstituicaoPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/instituicoes/:id/mensagem-confirmacao"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <DefinirMensagemConfirmacao />
        </PrivateRoute>
      }
    />

    {/* POSTS & DESTAQUES & SLIDER */}
    <Route path="/instituicoes/:instituicaoId/posts" element={<PostsPage />} />
    <Route path="/instituicoes/:instituicaoId/posts/:postId" element={<PostDetalhePage />} />
    <Route
      path="/instituicoes/:instituicaoId/gerenciar-destaques"
      element={
        <PrivateRoute allowedRoles={['instituicao']}>
          <GerenciarDestaquesPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/instituicoes/:instituicaoId/gerenciar-slider"
      element={
        <PrivateRoute allowedRoles={['instituicao']}>
          <GerenciarSlidersPage />
        </PrivateRoute>
      }
    />

    {/* PRÉ-INSCRIÇÕES */}
    <Route
      path="/pre-inscricoes/:tipo/:id"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <ListaPreInscricoesPorClasseCursoPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/pre-inscricoes"
      element={
        <PrivateRoute>
          <ListaPreInscricoesPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/pre-inscricoes/criar"
      element={
        <PrivateRoute allowedRoles={['estudante']}>
          <CriarPreInscricaoPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/pre-inscricoes/:id"
      element={
        <PrivateRoute>
          <DetalhesPreInscricaoPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/pre-inscricao-comprovativo/:id"
      element={
        <PrivateRoute allowedRoles={['estudante']}>
          <ComprovativoPreInscricaoPage />
        </PrivateRoute>
      }
    />

    {/* ESTUDANTES */}
    <Route
      path="/estudantes"
      element={
        <PrivateRoute allowedRoles={['admin']}>
          <ListaEstudantesPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/estudantes/:id"
      element={
        <PrivateRoute allowedRoles={['admin']}>
          <DetalhesEstudantePage />
        </PrivateRoute>
      }
    />
    <Route
      path="/estudantes/criar"
      element={
        <PrivateRoute allowedRoles={['admin']}>
          <CriarEditarEstudantePage />
        </PrivateRoute>
      }
    />
    <Route
      path="/estudantes/editar/:id"
      element={
        <PrivateRoute allowedRoles={['admin']}>
          <CriarEditarEstudantePage />
        </PrivateRoute>
      }
    />

    {/* CAMPANHAS */}
    <Route
      path="/campanhas"
      element={
        <PrivateRoute>
          <ListaCampanhasPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/campanhas/criar"
      element={
        <PrivateRoute allowedRoles={['instituicao', 'admin']}>
          <CriarEditarCampanhaPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/campanhas/editar/:id"
      element={
        <PrivateRoute allowedRoles={['instituicao', 'admin']}>
          <CriarEditarCampanhaPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/campanhas/:id"
      element={
        <PrivateRoute>
          <DetalhesCampanhaPage />
        </PrivateRoute>
      }
    />

{/* =================== CRECHE =================== */}
<Route
  path="/creche"
  element={
    <PrivateRoute
      allowedRoles={['admin', 'instituicao']}
      requiredModules={['CRECHE']}
    >
      <DashboardCrechePage />
    </PrivateRoute>
  }
/>

<Route
  path="/creche/matriculas"
  element={
    <PrivateRoute
      allowedRoles={['admin', 'instituicao']}
      requiredModules={['CRECHE']}
    >
      <ListaMatriculasCrechePage />
    </PrivateRoute>
  }
/>

<Route
  path="/creche/matriculas/nova"
  element={
    <PrivateRoute
      allowedRoles={['admin', 'instituicao']}
      requiredModules={['CRECHE']}
    >
      <NovaMatriculaCrechePage />
    </PrivateRoute>
  }
/>

<Route
  path="/creche/checkins"
  element={
    <PrivateRoute
      allowedRoles={['admin', 'instituicao']}
      requiredModules={['CRECHE']}
    >
      <CheckinsPage />
    </PrivateRoute>
  }
/>

<Route
  path="/creche/presencas"
  element={
    <PrivateRoute
      allowedRoles={['admin', 'instituicao']}
      requiredModules={['CRECHE']}
    >
      <PresencasPage />
    </PrivateRoute>
  }
/>

<Route
  path="/creche/responsaveis"
  element={
    <PrivateRoute
      allowedRoles={['admin', 'instituicao']}
      requiredModules={['CRECHE']}
    >
      <ResponsaveisPage />
    </PrivateRoute>
  }
/>

<Route
  path="/creche/saude"
  element={
    <PrivateRoute
      allowedRoles={['admin', 'instituicao']}
      requiredModules={['CRECHE']}
    >
      <SaudePage />
    </PrivateRoute>
  }
/>

<Route
  path="/creche/relatorios"
  element={
    <PrivateRoute
      allowedRoles={['admin', 'instituicao']}
      requiredModules={['CRECHE']}
    >
      <RelatoriosCrechePage />
    </PrivateRoute>
  }
/>
<Route
  path="/responsavel/creche"
  element={
    <PrivateRoute
      allowedRoles={['responsavel', 'instituicao', 'admin']}
      requiredModules={['CRECHE']}
    >
      <ResponsavelDashboardPage />
    </PrivateRoute>
  }
/>
<Route
  path="/creche/pre-inscricoes"
  element={
    <PrivateRoute
      allowedRoles={['admin', 'instituicao']}
      requiredModules={['CRECHE']}
    >
      <PreInscricoesAdminPage />
    </PrivateRoute>
  }
/>

    {/* RELATÓRIOS */}
    <Route
      path="/relatorios/geral"
      element={
        <PrivateRoute allowedRoles={['admin']}>
          <RelatorioGeralPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/relatorios/financeiro/:instituicaoId"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <RelatorioFinanceiroPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/relatorios/financeiro/:instituicaoId/inadimplencia"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <RelatorioFinanceiroPage defaultTab="inadimplencia" />
        </PrivateRoute>
      }
    />
    <Route
      path="/relatorios/financeiro/:instituicaoId/metodos-pagamento"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <RelatorioFinanceiroPage defaultTab="metodos-pagamento" />
        </PrivateRoute>
      }
    />
    <Route
      path="/relatorios/instituicao/:id"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <RelatorioInstituicaoPage />
        </PrivateRoute>
      }
    />

    {/* CURSOS / PERÍODOS / CLASSES / DISCIPLINAS */}
    <Route
      path="/cursos"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <ListaCursosPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/cursos/:id"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <DetalhesCursoPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/cursos/criar"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <CriarEditarCursoPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/cursos/editar/:id"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <CriarEditarCursoPage />
        </PrivateRoute>
      }
    />
    {/* Períodos */}
    <Route
      path="/periodos"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <ListaPeriodosPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/periodos/:id"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <DetalhesPeriodoPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/periodos/criar"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <CriarEditarPeriodoPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/periodos/editar/:id"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <CriarEditarPeriodoPage />
        </PrivateRoute>
      }
    />
    {/* Classes */}
    <Route
      path="/classes"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <ListaClassesPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/classes/:id"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <DetalhesClassePage />
        </PrivateRoute>
      }
    />
    <Route
      path="/classes/criar"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <CriarEditarClassePage />
        </PrivateRoute>
      }
    />
    <Route
      path="/classes/editar/:id"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <CriarEditarClassePage />
        </PrivateRoute>
      }
    />
    {/* Disciplinas */}
    <Route
      path="/disciplinas"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <ListaDisciplinasPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/disciplinas/:id"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <DetalhesDisciplinaPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/disciplinas/criar"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <CriarEditarDisciplinaPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/disciplinas/editar/:id"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <CriarEditarDisciplinaPage />
        </PrivateRoute>
      }
    />

    {/* COMPLETAR PERFIL */}
    <Route
      path="/completar-perfil/:tipo"
      element={
        <PrivateRoute>
          <CompletarPerfilPage />
        </PrivateRoute>
      }
    />

    {/* PLANOS DE PAGAMENTO */}
    <Route
      path="/planos-pagamento"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']} requiredModules={['PAGAMENTOS']}>
          <ListaPlanosPagamento />
        </PrivateRoute>
      }
    />
    <Route
      path="/planos-pagamento/criar"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']} requiredModules={['PAGAMENTOS']}>
          <CriarPlanoPagamento />
        </PrivateRoute>
      }
    />
    <Route
      path="/planos-pagamento/selecionar-base"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']} requiredModules={['PAGAMENTOS']}>
          <SelecionarPlanoBasePage />
        </PrivateRoute>
      }
    />
    <Route
      path="/planos-pagamento/:id"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']} requiredModules={['PAGAMENTOS']}>
          <DetalhesPlanoPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/planos-pagamento/editar/:id"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']} requiredModules={['PAGAMENTOS']}>
          <EditarPlanoPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/planos-pagamento/:planoId/vincular-estudante"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']} requiredModules={['PAGAMENTOS']}>
          <VincularEstudantePage />
        </PrivateRoute>
      }
    />
    <Route
      path="/planos-pagamento/:planoId/estudantes"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']} requiredModules={['PAGAMENTOS']}>
          <EstudantesPlanoLista />
        </PrivateRoute>
      }
    />
    <Route
      path="/planos-pagamento/:planoId/estudante/:estudanteId"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']} requiredModules={['PAGAMENTOS']}>
          <EstudantesPlanoDetalhes />
        </PrivateRoute>
      }
    />
    <Route
      path="/estudante/planos-pagamento"
      element={
        <PrivateRoute allowedRoles={['estudante']} requiredModules={['PAGAMENTOS']}>
          <PlanosPagamentoEstudante />
        </PrivateRoute>
      }
    />

    {/* RECOMENDAÇÕES */}
    <Route
      path="/recomendacoes"
      element={
        <PrivateRoute allowedRoles={['estudante']}>
          <RecomendacoesPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/recomendacoes/perfil"
      element={
        <PrivateRoute allowedRoles={['estudante']}>
          <RecomendacoesPage />
        </PrivateRoute>
      }
    />

    {/* =================== TCC / PROPOSTAS =================== */}
    <Route
      path="/propostas"
      element={
        <PrivateRoute
          allowedRoles={['admin', 'instituicao', 'supervisor', 'chefe_departamento', 'revisor', 'direcao_cientifica', 'estudante']}
          requiredModules={['TCC_MONOGRAFIAS']}
          requiresVinculo
        >
          <PropostaPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/propostas/submeter"
      element={
        <PrivateRoute allowedRoles={['estudante']} requiredModules={['TCC_MONOGRAFIAS']} requiresVinculo>
          <SubmeterPropostaPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/propostas/:id"
      element={
        <PrivateRoute
          allowedRoles={['admin', 'instituicao', 'chefe_departamento', 'revisor', 'supervisor', 'direcao_cientifica', 'estudante']}
          requiredModules={['TCC_MONOGRAFIAS']}
        >
          <PropostaDetalhesPage />
        </PrivateRoute>
      }
    />

    {/* =================== PROJETOS =================== */}
    <Route
      path="/projetos"
      element={
        <PrivateRoute
          allowedRoles={['admin', 'instituicao', 'chefe_departamento', 'revisor_projeto', 'revisor', 'supervisor', 'direcao_cientifica', 'estudante']}
          requiredModules={['TCC_MONOGRAFIAS']}
          requiresVinculo
        >
          <ProjetoPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/projetos/submeter"
      element={
        <PrivateRoute allowedRoles={['estudante', 'instituicao']} requiredModules={['TCC_MONOGRAFIAS']} requiresVinculo>
          <SubmeterProjetoPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/projetos/:id"
      element={
        <PrivateRoute
          allowedRoles={['admin', 'instituicao', 'chefe_departamento', 'revisor_projeto', 'revisor', 'supervisor', 'direcao_cientifica', 'estudante']}
          requiredModules={['TCC_MONOGRAFIAS']}
        >
          <ProjetoDetailsPage />
        </PrivateRoute>
      }
    />

    {/* =================== MONOGRAFIAS =================== */}
    <Route
      path="/monografias"
      element={
        <PrivateRoute
          allowedRoles={['admin', 'instituicao', 'chefe_departamento', 'revisor_projeto', 'revisor', 'arguente', 'presidente', 'supervisor', 'direcao_cientifica', 'estudante']}
          requiredModules={['TCC_MONOGRAFIAS']}
          requiresVinculo
        >
          <MonografiaPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/monografias/submeter"
      element={
        <PrivateRoute allowedRoles={['estudante']} requiredModules={['TCC_MONOGRAFIAS']} requiresVinculo>
          <SubmeterMonografiaPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/monografias/:id"
      element={
        <PrivateRoute
          allowedRoles={['admin', 'instituicao', 'chefe_departamento', 'revisor_projeto', 'revisor', 'supervisor', 'arguente', 'presidente', 'direcao_cientifica', 'estudante']}
          requiredModules={['TCC_MONOGRAFIAS']}
        >
          <MonografiaDetails />
        </PrivateRoute>
      }
    />

    {/* =================== DEFESAS =================== */}
    <Route
      path="/defesas"
      element={
        <PrivateRoute
          allowedRoles={['instituicao', 'estudante', 'presidente', 'chefe_departamento', 'revisor_projeto', 'revisor', 'supervisor', 'arguente', 'direcao_cientifica']}
          requiredModules={['TCC_MONOGRAFIAS']}
        >
          <DefesaPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/defesas/agendar"
      element={
        <PrivateRoute
          allowedRoles={['instituicao', 'estudante', 'presidente', 'chefe_departamento', 'revisor', 'arguente', 'supervisor', 'direcao_cientifica']}
          requiredModules={['TCC_MONOGRAFIAS']}
        >
          <AgendarDefesaPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/defesas/:id"
      element={
        <PrivateRoute
          allowedRoles={['instituicao', 'estudante', 'presidente', 'revisor_projeto', 'chefe_departamento', 'revisor', 'arguente', 'supervisor', 'direcao_cientifica']}
          requiredModules={['TCC_MONOGRAFIAS']}
        >
          <DefesaDetails />
        </PrivateRoute>
      }
    />

    {/* =================== AVALIAÇÕES =================== */}
    <Route
      path="/avaliacoes"
      element={
        <PrivateRoute
          allowedRoles={['admin', 'instituicao', 'presidente', 'supervisor', 'arguente', 'direcao_cientifica', 'avaliador']}
        >
          <AvaliacaoPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/avaliacoes/registrar"
      element={
        <PrivateRoute
          allowedRoles={['avaliador', 'presidente', 'supervisor', 'arguente', 'direcao_cientifica']}
        >
          <RegistrarAvaliacaoPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/avaliacoes/nova/:defesaId"
      element={
        <PrivateRoute allowedRoles={['arguente', 'presidente', 'supervisor']}>
          <RegistrarAvaliacaoPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/avaliacoes/:id"
      element={
        <PrivateRoute
          allowedRoles={['admin', 'instituicao', 'arguente', 'supervisor', 'presidente', 'direcao_cientifica', 'avaliador', 'estudante']}
        >
          <AvaliacaoDetails />
        </PrivateRoute>
      }
    />

    {/* =================== PRESIDENTES =================== */}
    <Route
      path="/presidentes"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <PresidentePage />
        </PrivateRoute>
      }
    />
    <Route
      path="/presidentes/nomear"
      element={
        <PrivateRoute allowedRoles={['admin']}>
          <NomearPresidentePage />
        </PrivateRoute>
      }
    />
    <Route
      path="/presidentes/:id"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <PresidenteDetails />
        </PrivateRoute>
      }
    />

    {/* =================== ARGUENTES =================== */}
    <Route
      path="/arguentes"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <ArguentePage />
        </PrivateRoute>
      }
    />
    <Route
      path="/arguentes/registrar"
      element={
        <PrivateRoute allowedRoles={['admin']}>
          <RegistrarArguentePage />
        </PrivateRoute>
      }
    />
    <Route
      path="/arguentes/:id"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <ArguenteDetails />
        </PrivateRoute>
      }
    />

    {/* =================== SUPERVISORES =================== */}
    <Route
      path="/supervisores"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao']}>
          <SupervisorPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/supervisores/registrar"
      element={
        <PrivateRoute allowedRoles={['admin']}>
          <RegistrarSupervisorPage />
        </PrivateRoute>
      }
    />

    <Route path="/tcc" element={<TCCPage />} />
    <Route
      path="/tcc/overview"
      element={
        <PrivateRoute
          allowedRoles={['instituicao', 'chefe_departamento', 'supervisor', 'revisor', 'revisor_projeto', 'arguente', 'presidente', 'estudante']}
          requiredModules={['TCC_MONOGRAFIAS']}
        >
          <TCCHomePage />
        </PrivateRoute>
      }
    />
    <Route path="/integridade" element={<IntegridadePage />} />

    {/* =================== UNIDADES ORGÂNICAS =================== */}
    <Route
      path="/unidades-organicas"
      element={
        <PrivateRoute allowedRoles={['admin']}>
          <UnidadeOrganicaPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/unidades-organicas/criar"
      element={
        <PrivateRoute allowedRoles={['admin']}>
          <CriarUnidadeOrganicaPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/unidades-organicas/:id"
      element={
        <PrivateRoute allowedRoles={['admin']}>
          <UnidadeOrganicaDetails />
        </PrivateRoute>
      }
    />

    {/* =================== NOTIFICAÇÕES ACADÉMICAS =================== */}
    <Route
      path="/notificacoes"
      element={
        <PrivateRoute allowedRoles={['admin', 'instituicao', 'estudante']}>
          <NotificacaoPage />
        </PrivateRoute>
      }
    />

    {/* =================== DASHBOARDS ESPECÍFICOS =================== */}
    <Route
      path="/dashboard/chefe-departamento"
      element={
        <PrivateRoute allowedRoles={['chefe_departamento']}>
          <ChefeDepartamentoDashboardPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/dashboard/revisor"
      element={
        <PrivateRoute allowedRoles={['revisor_projeto']}>
          <RevisorDashboardPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/dashboard/cientifico"
      element={
        <PrivateRoute allowedRoles={['DIRECAO_CIENTIFICA', 'direcao_cientifica']}>
          <CientificoDashboardPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/dashboard/supervisor"
      element={
        <PrivateRoute allowedRoles={['supervisor']}>
          <SupervisorDashboardPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/dashboard/presidente"
      element={
        <PrivateRoute allowedRoles={['presidente']}>
          <PresidenteDashboardPage />
        </PrivateRoute>
      }
    />
    <Route
      path="/dashboard/arguente"
      element={
        <PrivateRoute allowedRoles={['arguente']}>
          <ArguenteDashboardPage />
        </PrivateRoute>
      }
    />

    <Route
      path="/dashboard/tcc"
      element={
        <PrivateRoute
          allowedRoles={['admin', 'instituicao', 'supervisor', 'estudante']}
          requiredModules={['TCC_MONOGRAFIAS']}
        >
          <DashboardTCC />
        </PrivateRoute>
      }
    />

    {/* =================== REPOSITÓRIO DIGITAL =================== */}
    <Route
      path="/repositorio"
      element={<RepositorioPage />}
    />

    {/* Fallback */}
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

export default AppRouter;
